import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';

@Component({
  standalone: true,
  selector: 'app-range-h-bar',
  imports: [CommonModule],
  templateUrl: './range-h-bar.component.html',
  styleUrls: ['./range-h-bar.component.scss']
})
export class RangeHBarComponent implements AfterViewInit, OnChanges {

  @ViewChild('barSvg', { static: true }) barSvgRef!: ElementRef<SVGSVGElement>;
  @ViewChild('tooltipSvg', { static: true }) tooltipSvgRef!: ElementRef<SVGSVGElement>;
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  @Input() data:any;
  @Input() selectedMapConfig:any;
  min = 0;
  max = 100;
  
  label?: string;
  colors: string[] = ['#CEE3FF', '#5a9bf0', '#0d47a1'];

  @Output() hoverValueChange = new EventEmitter<number>();
  
  private width = 200;
  private height = 40;
  private barHeight = 6;

  private barSvg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private tooltipSvg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;

  private tooltip!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private tooltipRect!: d3.Selection<SVGRectElement, unknown, null, undefined>;
  private tooltipText!: d3.Selection<SVGTextElement, unknown, null, undefined>;
  private tooltipNotch!: d3.Selection<SVGPathElement, unknown, null, undefined>;

  private xScale!: d3.ScaleLinear<number, number>;
  private colorScale!: d3.ScaleLinear<string, string>;

  private barOffsetY = 0;

  ngAfterViewInit(): void {
    this.initSvg();
    this.draw();
    this.updateFromValue();
  }

  ngOnChanges(_: SimpleChanges): void {
    
    let cfg = this.selectedMapConfig;
    this.colors = cfg?.colorBar || this.colors;
    this.label = cfg?.rangeLabel || "";
// debugger
    if(this.data.length>0) {
      // Extract values
      const values = this.data.map((item:any) => item.value);
      // Find min and max
      this.min = Math.min(...values);
      this.max = Math.max(...values);
      if (this.min === this.max) {
        this.min = 0;
      }
    } else {
      this.min = 0;
      this.max = 0;
    }

    if(this.max == 0) {
     this.colors = ['#CEE3FF', '#CEE3FF', '#CEE3FF']
    }

    if (this.barSvg) {
      if(this.max == 0) {
       this.colors = ['#CEE3FF', '#CEE3FF', '#CEE3FF']
      }
      this.draw();
      this.updateFromValue();
    }
  }

  /* ---------- Init ---------- */
  private initSvg(): void {
    this.barSvg = d3.select(this.barSvgRef.nativeElement)
      .attr('width', this.width)
      .attr('height', this.height);

    this.tooltipSvg = d3.select(this.tooltipSvgRef.nativeElement)
      .attr('width', '100%')
      .attr('height', '100%');

    const barRect = this.barSvgRef.nativeElement.getBoundingClientRect();
    const containerRect = this.containerRef.nativeElement.getBoundingClientRect();

    this.barOffsetY = barRect.top - containerRect.top;
  }

  /* ---------- Draw ---------- */
  private draw(): void {
    this.barSvg.selectAll('*').remove();
    this.tooltipSvg.selectAll('*').remove();

    this.xScale = d3.scaleLinear()
      .domain([this.min, this.max])
      .range([0, this.width])
      .clamp(true);

    this.colorScale = d3.scaleLinear<string>()
      .domain(this.colors.map((_, i) =>
        this.min + (i / (this.colors.length - 1)) * (this.max - this.min)
      ))
      .range(this.colors);

    const defs = this.barSvg.append('defs');
    const gradient = defs.append('linearGradient')
      .attr('id', 'rangeGradient')
      .attr('x1', '0%')
      .attr('x2', '100%');

    this.colors.forEach((c, i) => {
      gradient.append('stop')
        .attr('offset', `${(i / (this.colors.length - 1)) * 100}%`)
        .attr('stop-color', c);
    });

    const barY = this.height / 2;

    this.barSvg.append('rect')
      .attr('x', 0)
      .attr('y', barY - this.barHeight / 2)
      .attr('width', this.width)
      .attr('height', this.barHeight)
      .attr('rx', 4)
      .attr('fill', 'url(#rangeGradient)')
      .style('cursor', 'pointer');

    const hitPad = 6;
    const hitArea = this.barSvg.append('rect')
      .attr('x', -hitPad)
      .attr('y', 0)
      .attr('width', this.width + hitPad * 2)
      .attr('height', this.height)
      .attr('fill', 'transparent')
      .style('pointer-events', 'all')
      .style('cursor', 'pointer')
      .on('mousemove', (event: MouseEvent) => {
        const rect = (hitArea.node() as SVGRectElement).getBoundingClientRect();
        const x = Math.max(0, Math.min(this.width, event.clientX - rect.left - hitPad));
        const value = Math.round(this.xScale.invert(x));
        this.showTooltip(event.clientX, barY, value, 'top');
        this.hoverValueChange.emit(value);
      })
      .on('mouseleave', () => {
        this.hoverValueChange.emit(undefined!);
        this.hideTooltip();
        this.updateFromValue();
      });

    /* Tooltip */
    this.tooltip = this.tooltipSvg.append('g')
      .style('display', 'none');

    this.tooltipRect = this.tooltip.append('rect').attr('rx', 4);

    this.tooltipText = this.tooltip.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', 10)
      .attr('font-weight', 500)
      .attr('fill', '#fff');

    this.tooltipNotch = this.tooltip.append('path');
  }

  /* ---------- Tooltip ---------- */
  private showTooltip(
    clientX: number,
    barY: number,
    value: number,
    position: 'top' | 'bottom'
  ): void {

    const containerRect =
      this.containerRef.nativeElement.getBoundingClientRect();

    const tooltipX = clientX - containerRect.left;
    const color = this.colorScale(value);

    this.tooltipText.text(value.toString());

    const bbox = this.tooltipText.node()!.getBBox();
    const padX = 4;
    const padY = 2;
    const notch = 5;
    const gap = 2;

    const w = bbox.width + padX * 2;
    const h = bbox.height + padY * 2;

    if (position === 'top') {
      this.tooltipText.attr('x', 0).attr('y', -(h / 2 + notch));

      this.tooltipRect
        .attr('x', -w / 2)
        .attr('y', -(h + notch))
        .attr('width', w)
        .attr('height', h)
        .attr('fill', color);

      this.tooltipNotch
        .attr('d', `M 0 0 L ${-notch} ${-notch} L ${notch} ${-notch} Z`)
        .attr('fill', color);

      this.tooltip.attr(
        'transform',
        `translate(${tooltipX}, ${this.barOffsetY + barY - this.barHeight - gap})`
      );
    } else {
      this.tooltipText.attr('x', 0).attr('y', h / 2 + notch);

      this.tooltipRect
        .attr('x', -w / 2)
        .attr('y', notch)
        .attr('width', w)
        .attr('height', h)
        .attr('fill', color);

      this.tooltipNotch
        .attr('d', `M 0 0 L ${-notch} ${notch} L ${notch} ${notch} Z`)
        .attr('fill', color);

      this.tooltip.attr(
        'transform',
        `translate(${tooltipX}, ${this.barOffsetY + barY + this.barHeight + gap})`
      );
    }

    this.tooltip.style('display', null);
  }

  private hideTooltip(): void {
    this.tooltip.style('display', 'none');
  }

  /* ---------- External value ---------- */
  private updateFromValue(): void {
    // if (this.value == null) return;

    // const barRect = this.barSvgRef.nativeElement.getBoundingClientRect();
    // const x = this.xScale(this.value);

    // this.showTooltip(barRect.left + x, this.height / 2, this.value, 'bottom');
  }
}
